import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowRight, Bell, CalendarClock, Check, CreditCard, Eye, Loader2, LogOut, MessageSquare, Trash2,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import TierBadge from "../components/TierBadge";
import IdeasBox from "../components/IdeasBox";
import AdminReaderPreview, { ACCOUNT_VIEWS, readerViewLabel } from "../components/AdminReaderPreview";
import { TIER_LABEL, TIER_ORDER, tierStyle, tierCovers } from "../lib/tiers";
import { CONTACT_EMAIL, DATA_DELETION_DAYS } from "../lib/siteConfig";

const PROVIDER_LABEL = { google: "Google", password: "E-mail et mot de passe" };

// Ce que l'offre suivante apporte en plus (encart d'invitation)
const NEXT_GAIN = {
  essentiel: "tous les portefeuilles Side B, Monte Carlo et Liquidation Map",
  pro: "la communauté WhatsApp privée",
};

function fmtDate(d) {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return d;
  }
}
const inDays = (n) => new Date(Date.now() + n * 864e5).toISOString();
const fmtPct = (n) =>
  n === null || n === undefined || isNaN(n)
    ? "—"
    : `${n >= 0 ? "+" : "−"}${Math.abs(n).toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;

export default function AccountPage() {
  const { user, setUserDirect, logout } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const isAdmin = !!(user && user.role === "admin");
  const [acct, setAcct] = useState(user);
  const [portalLoading, setPortalLoading] = useState(false);

  // Aperçu admin : affiche la carte d'offre comme un abonné (dates d'exemple)
  const [previewAs, setPreviewAs] = useState("admin");
  const isPreviewing = isAdmin && previewAs !== "admin";

  const { data: cfg } = useQuery({
    queryKey: ["config"],
    queryFn: async () => (await api.get("/config")).data,
  });
  const { data: followed = [], isLoading: followedLoading } = useQuery({
    queryKey: ["sideb-following"],
    queryFn: async () => (await api.get("/sideb/following")).data,
  });
  // Lien de la communauté WhatsApp privée : renvoyé par le serveur aux seuls abonnés Max (et admins)
  const { data: canal } = useQuery({
    queryKey: ["me-canal"],
    queryFn: async () => (await api.get("/me/canal")).data,
    enabled: !!user,
  });
  const { data: twofa } = useQuery({
    queryKey: ["2fa-status"],
    queryFn: async () => (await api.get("/auth/2fa/status")).data,
  });

  useEffect(() => {
    // Synchronise l'état réel depuis Stripe (fiable même si le webhook n'arrive pas)
    api
      .post("/payments/sync")
      .then((r) => {
        setAcct(r.data);
        setUserDirect(r.data);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unfollowMut = useMutation({
    mutationFn: async (pid) => (await api.delete(`/sideb/portfolios/${pid}/follow`)).data,
    onSuccess: () => {
      toast.success("Vous ne suivez plus ce portefeuille");
      qc.invalidateQueries({ queryKey: ["sideb-following"] });
    },
    onError: () => toast.error("Impossible de modifier le suivi"),
  });

  // Pseudo auteur (admins) : modifiable ici, se répercute sur tous leurs contenus
  const [pseudoInput, setPseudoInput] = useState("");
  const [editingPseudo, setEditingPseudo] = useState(false);
  useEffect(() => {
    setPseudoInput(acct?.pseudo || "");
  }, [acct?.pseudo]);
  const pseudoMut = useMutation({
    mutationFn: async (p) => (await api.patch("/account/pseudo", { pseudo: p })).data,
    onSuccess: (d) => {
      toast.success("Pseudo mis à jour");
      setAcct((a) => ({ ...(a || {}), pseudo: d.pseudo }));
      setUserDirect({ ...(user || {}), pseudo: d.pseudo });
      setEditingPseudo(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Impossible de modifier le pseudo"),
  });

  // Données affichées : réelles, ou simulées en aperçu admin
  let tier = acct?.subscription_tier || "free";
  let cancelAt = acct?.subscription_cancel_at;
  let renewAt = acct?.subscription_expires_at;
  let nextTier = acct?.subscription_next_tier;
  let nextTierAt = acct?.subscription_next_tier_at;
  if (isPreviewing) {
    tier = previewAs === "canceled" ? "pro" : previewAs;
    cancelAt = previewAs === "canceled" ? inDays(18) : null;
    renewAt = tier === "free" ? null : inDays(18);
    nextTier = null;
    nextTierAt = null;
  }
  const isPaid = tier !== "free";
  const ideasCovered = tierCovers(tier, "essentiel");
  const s = tierStyle(tier);
  const meta = cfg?.tiers?.[tier];
  const price = meta && meta.price_eur > 0 ? `${meta.price_eur.toString().replace(".", ",")} €` : null;
  const next = TIER_ORDER[TIER_ORDER.indexOf(tier) + 1];
  const firstName = (acct?.name || "").split(" ")[0];

  const guardPreview = () => {
    if (isPreviewing) {
      toast.info("Bouton désactivé pendant l'aperçu");
      return true;
    }
    return false;
  };

  const openPortal = async () => {
    if (guardPreview()) return;
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'ouvrir l'espace d'abonnement");
    } finally {
      setPortalLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  const deletionHref = CONTACT_EMAIL
    ? `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Demande de suppression de compte")}&body=${encodeURIComponent(
        `Bonjour,\n\nJe souhaite la suppression de mon compte Side B et de mes données personnelles.\nAdresse du compte : ${acct?.email || ""}\n\nMerci.`
      )}`
    : null;

  return (
    <div className="min-h-screen">
      {isPreviewing && (
        <div className="border-b border-violet-500/20 bg-violet-500/[0.08]" data-testid="admin-preview-banner">
          <div className="mx-auto max-w-5xl px-6 py-2.5 flex flex-wrap items-center justify-between gap-3 text-xs">
            <span className="text-violet-200 flex items-center gap-2">
              <Eye className="w-3.5 h-3.5" /> Aperçu admin : votre offre telle que la voit un
              <b className="font-semibold text-white">{readerViewLabel(previewAs, ACCOUNT_VIEWS)}</b>
              · dates d'exemple, profil et portefeuilles suivis = les vôtres
            </span>
            <button
              type="button"
              onClick={() => setPreviewAs("admin")}
              className="text-violet-300 hover:text-white underline-offset-4 hover:underline"
            >
              Revenir à ma vue
            </button>
          </div>
        </div>
      )}

      <section className="border-b border-white/5">
        <div className="mx-auto max-w-5xl px-6 py-8 sm:py-14">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-2 sm:mb-3">Mon compte</div>
          <h1 className="font-display font-black text-[2rem] leading-tight sm:text-5xl">{firstName ? `Bonjour ${firstName}` : "Mon compte"}</h1>
          <p className="mt-3 text-zinc-400">Votre abonnement, vos portefeuilles suivis et la sécurité de votre compte.</p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pt-6 pb-8 sm:py-10 grid gap-6 lg:grid-cols-[1.5fr_1fr] items-start">
        <div className="space-y-6">
          {/* Abonnement */}
          <div className={`rounded-xl border bg-card p-6 ${s.border}`} data-testid="account-subscription-card">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Votre offre</div>
                <div className="mt-1 flex items-center gap-3">
                  <span className={`font-display font-black text-3xl ${s.title}`} data-testid="account-tier">
                    {TIER_LABEL[tier] || tier}
                  </span>
                  <span className="text-sm text-zinc-500">{price ? `${price} / mois` : "gratuit"}</span>
                </div>
              </div>
              <TierBadge tier={tier} />
            </div>

            {isPaid &&
              (cancelAt ? (
                <div
                  className="mt-5 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-4 flex gap-3"
                  data-testid="account-cancel-notice"
                >
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" />
                  <div className="text-sm">
                    <div className="text-amber-200 font-medium">Abonnement résilié</div>
                    <p className="mt-0.5 text-zinc-400">
                      Vous gardez l'accès à l'offre {TIER_LABEL[tier]} jusqu'au{" "}
                      <b className="text-zinc-200">{fmtDate(cancelAt)}</b>. Aucun nouveau prélèvement ne sera effectué.
                    </p>
                    <button
                      type="button"
                      onClick={openPortal}
                      className="mt-2 inline-flex items-center gap-1.5 text-amber-200 hover:text-white font-medium"
                      data-testid="account-reactivate-btn"
                    >
                      Réactiver mon abonnement <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ) : nextTier ? (
                <div
                  className="mt-5 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-4 flex gap-3"
                  data-testid="account-next-tier-notice"
                >
                  <CalendarClock className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" />
                  <div className="text-sm">
                    <div className="text-amber-200 font-medium">Changement d'offre programmé</div>
                    <p className="mt-0.5 text-zinc-400">
                      Votre offre passera à <b className="text-zinc-200">{TIER_LABEL[nextTier] || nextTier}</b> le{" "}
                      <b className="text-zinc-200">{fmtDate(nextTierAt)}</b>.
                    </p>
                  </div>
                </div>
              ) : renewAt ? (
                <div className="mt-4 flex items-center gap-2 text-sm text-zinc-400" data-testid="account-renew-notice">
                  <CalendarClock className="w-4 h-4 text-zinc-500" /> Prochain renouvellement le{" "}
                  <span className="text-zinc-200">{fmtDate(renewAt)}</span>
                  {price ? ` · ${price}` : ""}
                </div>
              ) : null)}

            {meta?.features?.length > 0 && (
              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2">Inclus dans votre offre</div>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                  {meta.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${s.text}`} /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              {isPaid ? (
                <>
                  <button
                    type="button"
                    onClick={openPortal}
                    disabled={portalLoading}
                    className="inline-flex items-center gap-2 rounded-md text-sm font-medium h-10 px-4 shadow bg-emerald-500 text-black hover:bg-emerald-300 disabled:opacity-60"
                    data-testid="account-manage-btn"
                  >
                    {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                    Gérer mon abonnement
                  </button>
                  <Link
                    to="/upgrade"
                    className="inline-flex items-center gap-2 rounded-md text-sm h-10 px-4 border border-white/15 hover:bg-white/5"
                  >
                    Changer d'offre
                  </Link>
                </>
              ) : (
                <Link
                  to="/upgrade"
                  className="inline-flex items-center gap-2 rounded-md text-sm font-medium h-10 px-4 shadow bg-emerald-500 text-black hover:bg-emerald-300"
                  data-testid="account-upgrade-btn"
                >
                  Voir les offres <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
            {isPaid && (
              <p className="mt-3 text-xs text-zinc-500">
                Factures, moyen de paiement et résiliation se gèrent sur l'espace sécurisé Stripe.
              </p>
            )}

            {isPaid && next && !cancelAt && NEXT_GAIN[tier] && (
              <div
                className={`mt-6 rounded-lg border p-4 flex flex-wrap items-center justify-between gap-3 ${tierStyle(next).soft}`}
                data-testid="account-next-tier"
              >
                <p className="text-sm text-zinc-300">
                  Passez à <b className={tierStyle(next).title}>{TIER_LABEL[next]}</b> pour débloquer {NEXT_GAIN[tier]}.
                </p>
                <Link to="/upgrade" className={`inline-flex items-center gap-1.5 text-sm font-medium ${tierStyle(next).text} hover:text-white`}>
                  Découvrir <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            )}
          </div>

          {/* Communauté WhatsApp privée (abonnés Max) : lien d'invitation */}
          {canal?.url && !isPreviewing && (
            <div className="rounded-xl border border-violet-500/30 bg-violet-500/[0.06] p-6 flex flex-wrap items-center gap-4" data-testid="account-canal">
              <span className="w-10 h-10 rounded-full bg-violet-500/15 border border-violet-500/40 grid place-items-center text-violet-300 shrink-0">
                <MessageSquare className="w-5 h-5" />
              </span>
              <div className="flex-1 min-w-[12rem]">
                <h2 className="font-display font-bold text-lg">Votre communauté privée</h2>
                <p className="mt-0.5 text-sm text-zinc-400">
                  Annonces et discussions avec l'équipe Side B, sur WhatsApp. Ce lien vous est réservé : merci de ne pas le partager.
                </p>
              </div>
              <a
                href={canal.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-md text-sm font-medium ${tierStyle("max").cta}`}
                data-testid="account-canal-link"
              >
                Rejoindre sur WhatsApp <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          )}

          {/* Boîte à idées (abonnés payants + admins) */}
          <IdeasBox
            locked={isPreviewing ? !ideasCovered : !isAdmin && !ideasCovered}
            isPreviewing={isPreviewing}
            guardPreview={guardPreview}
            cfg={cfg}
          />

          {/* Portefeuilles suivis */}
          <div className="rounded-xl border border-white/5 bg-card p-6" data-testid="account-followed">
            <div className="flex items-center justify-between gap-3 mb-2">
              <h2 className="font-display font-bold text-lg">Portefeuilles suivis</h2>
              <span className="text-xs text-zinc-500 inline-flex items-center gap-1.5">
                <Bell className="w-3.5 h-3.5" /> Alerte à chaque mouvement
              </span>
            </div>
            {followedLoading ? (
              <div className="py-6 grid place-items-center">
                <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
              </div>
            ) : followed.length === 0 ? (
              <p className="py-6 text-sm text-zinc-500 text-center">Vous ne suivez aucun portefeuille.</p>
            ) : (
              followed.map((p) => (
                <div key={p.id} className="flex items-center gap-3 py-3 border-t border-white/5 first:border-0" data-testid="account-followed-row">
                  <div className="min-w-0">
                    <Link to={`/portefeuilles-sideb/${p.slug}`} className="text-sm text-zinc-100 hover:text-emerald-300">
                      {p.name}
                    </Link>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <TierBadge tier={p.tier_required} />
                      <span className={`text-xs font-mono-num ${(p.total_return_pct ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                        {fmtPct(p.total_return_pct)}
                      </span>
                      {!p.covered && <span className="text-[11px] text-amber-300">Hors de votre offre · alertes suspendues</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => !guardPreview() && unfollowMut.mutate(p.id)}
                    disabled={unfollowMut.isPending}
                    className="ml-auto text-xs text-zinc-500 hover:text-zinc-200"
                  >
                    Ne plus suivre
                  </button>
                </div>
              ))
            )}
            <Link to="/portefeuilles-sideb" className="mt-3 inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300">
              Voir tous les portefeuilles <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        <div className="space-y-6">
          {/* Profil et sécurité */}
          <div className="rounded-xl border border-white/5 bg-card p-6" data-testid="account-profile">
            <h2 className="font-display font-bold text-lg mb-4">Profil et sécurité</h2>
            {[
              ["Nom", acct?.name || "—"],
              ["E-mail", acct?.email || "—"],
              ["Connexion", PROVIDER_LABEL[acct?.provider] || acct?.provider || "—"],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between gap-3 py-2.5 border-t border-white/5 first:border-0 text-sm">
                <span className="text-zinc-500">{label}</span>
                <span className="text-zinc-200 truncate">{value}</span>
              </div>
            ))}
            {isAdmin && (
              <div className="py-2.5 border-t border-white/5 text-sm" data-testid="account-pseudo">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-zinc-500">Pseudo auteur</span>
                  {!editingPseudo && (
                    <span className="inline-flex items-center gap-3">
                      <span className="text-zinc-200">{acct?.pseudo || "Side B"}</span>
                      <button
                        type="button"
                        onClick={() => setEditingPseudo(true)}
                        className="text-emerald-400 hover:text-emerald-300"
                        data-testid="account-pseudo-edit"
                      >
                        Modifier
                      </button>
                    </span>
                  )}
                </div>
                {editingPseudo && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      value={pseudoInput}
                      onChange={(e) => setPseudoInput(e.target.value)}
                      maxLength={30}
                      placeholder="Votre pseudo (2 à 30 caractères)"
                      className="flex-1 rounded-md bg-white/5 border border-white/10 px-3 h-9 text-sm text-zinc-100 focus:outline-none focus:border-emerald-500/50"
                      data-testid="account-pseudo-input"
                    />
                    <button
                      type="button"
                      onClick={() => pseudoMut.mutate(pseudoInput.trim())}
                      disabled={pseudoMut.isPending || pseudoInput.trim().length < 2}
                      className="inline-flex items-center gap-1.5 rounded-md h-9 px-3 text-sm font-medium bg-emerald-500 text-black hover:bg-emerald-300 disabled:opacity-60"
                      data-testid="account-pseudo-save"
                    >
                      {pseudoMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingPseudo(false);
                        setPseudoInput(acct?.pseudo || "");
                      }}
                      className="text-xs text-zinc-500 hover:text-zinc-200"
                    >
                      Annuler
                    </button>
                  </div>
                )}
                <p className="mt-1 text-xs text-zinc-600">
                  Affiché comme auteur de vos analyses et cours. Mettre à jour le met à jour partout.
                </p>
              </div>
            )}
            <div className="flex items-center justify-between gap-3 py-2.5 border-t border-white/5 text-sm">
              <span className="text-zinc-500">Double authentification</span>
              <span className="inline-flex items-center gap-3">
                <span className={twofa?.enabled ? "text-emerald-300" : "text-zinc-400"}>{twofa?.enabled ? "Activée" : "Désactivée"}</span>
                <Link to="/admin/securite" className="text-emerald-400 hover:text-emerald-300">
                  {twofa?.enabled ? "Gérer" : "Activer"}
                </Link>
              </span>
            </div>
          </div>

          <div className="rounded-xl border border-white/5 bg-card p-6 text-sm">
            <button type="button" onClick={handleLogout} className="flex items-center gap-2 text-zinc-300 hover:text-white" data-testid="account-logout-btn">
              <LogOut className="w-4 h-4" /> Se déconnecter
            </button>
            {deletionHref && (
              <div className="mt-4 pt-4 border-t border-white/5">
                <a href={deletionHref} className="inline-flex items-center gap-2 text-zinc-500 hover:text-rose-300" data-testid="account-delete-link">
                  <Trash2 className="w-4 h-4" /> Demander la suppression de mon compte
                </a>
                <p className="mt-1 text-xs text-zinc-600">
                  Vos données personnelles seront supprimées dans un délai de {DATA_DELETION_DAYS} jours. Pensez à résilier votre
                  abonnement avant.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {isAdmin && <AdminReaderPreview value={previewAs} onChange={setPreviewAs} views={ACCOUNT_VIEWS} />}
    </div>
  );
}
